import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-bulk-rounded";

import { Tooltip, TooltipTrigger, TooltipPopup } from "@/components/ui/tooltip";
import { compact } from "@/lib/format";
import { reachCaveats, type ReachValue } from "@/lib/use-reach";

/** A reach or engagement-rate figure. "…" while the platforms are asked, "—"
 *  where nothing applies, and — for reach — an info mark when part of the
 *  selection has no platform reach or is a sum the platform could not
 *  deduplicate. ER rests on impressions, so reach caveats do not apply to it. */
export function ReachFigure({ loading, value, format, reachCaveatsApply = true }: {
  loading: boolean;
  value: ReachValue | undefined;
  format: (v: ReachValue) => string;
  reachCaveatsApply?: boolean;
}) {
  if (loading) return <span className="text-muted-foreground" title="Bereik wordt bij de platformen opgevraagd">…</span>;
  if (!value) return <span>—</span>;
  const caveats = reachCaveatsApply ? reachCaveats(value) : [];
  // Every part has a figure but one is not deduplicated by its platform (an
  // Instagram period over 30 days, a sum of TikTok campaigns): an estimate.
  const estimate = reachCaveatsApply && value.complete && !value.exact;
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {estimate && "≈ "}{format(value)}
      {caveats.length > 0 && (
        <Tooltip>
          <TooltipTrigger delay={0} closeOnClick={false} aria-label="Toelichting bij dit cijfer"
            className="cursor-help rounded text-amber-600 dark:text-amber-400">
            <HugeiconsIcon icon={InformationCircleIcon} size={13} />
          </TooltipTrigger>
          <TooltipPopup className="max-w-xs text-xs leading-relaxed">
            {caveats.map((c) => <p key={c}>{c}</p>)}
          </TooltipPopup>
        </Tooltip>
      )}
    </span>
  );
}

/** Impressions. Instagram's row holds its Views, shown as impressions (Prado,
 *  25 Sep 2026); the definitions card on Awareness says so. */
export function ImpressionsFigure({ row }: { row: { key: string; impressions: number } }) {
  return <span>{row.impressions ? compact(row.impressions) : "—"}</span>;
}
